// ---------- cloud mirror ----------
// Keeps the local saves and the anonymous account's saves in step. Three moments:
//
//   launch   syncOnce()  — pull the index, then push whatever never made it out
//   playing  pushSoon()  — coalesced, called by progress.ts on every flush
//   opening  pullPuzzle() — only when the local record is known to be incomplete
//
// Nothing here is allowed to break the game. Every path swallows its errors: an unreachable
// backend, a full quota or a revoked token cost the cloud copy, never the session in progress.
//
// LES CRÉATIONS N'Y PASSENT PLUS (2026-10-30). Elles vivent sur le téléphone seul (cf.
// src/creationStore.ts), leur sauvegarde de partie aussi : aucune n'est poussée, tirée ni remise
// à zéro sur le serveur. Seule exception, settleCreation(), le rapatriement d'une création que le
// serveur gardait encore.

import {
  HttpError,
  Unauthorized,
  isCreation,
  deleteRemoteProgress,
  fetchProgressIndex,
  fetchRemoteProgress,
  pushRemoteProgress,
  reportClientError,
} from './api';
import { currentUser, ensureUser, forgetUser } from './account';
import {
  adoptRemote,
  forgetProgress,
  localSnapshots,
  mergeRemoteIndex,
  setProgressSync,
  setStorageFullReport,
  type Snapshot,
} from './progress';
import { flushShares } from './shares';

/**
 * Network coalescing, on top of the 800 ms that already coalesces the writes to localStorage.
 * A sweep fills dozens of cells a second, each one an entry in the list, and the push carries
 * the WHOLE list every time: pushing on each flush would re-upload a growing blob for nothing,
 * and the blob grows far faster now that a fill is a cell rather than a zone.
 *
 * Trente secondes et non dix, et ce n'est pas le volume qui l'a fait passer : c'est la RADIO.
 * Un envoi tire le modem hors de sa veille, et il y reste une dizaine de secondes après le
 * dernier octet avant de redescendre — une requête toutes les dix secondes ne le laissait donc
 * jamais se rendormir de toute une partie, et un modem éveillé coûte plus cher que ce qu'il
 * transporte ici.
 *
 * Rien n'est risqué à attendre : la sauvegarde locale est intacte pendant tout ce temps, et la
 * fenêtre ne se vide pas d'elle-même — `flushSync()` la force au moment où le joueur passe à
 * une autre application, qui est la façon dont une partie se termine sur un téléphone. Seul un
 * arrêt brutal du processus la perdrait, et le prochain lancement la rattraperait de toute
 * façon : la liste part entière à chaque fois, jamais en différentiel.
 *
 * Deux minutes et non plus trente secondes, et cette fois c'est le QUOTA. Chaque envoi est une
 * ligne écrite dans D1, et cette route faisait à elle seule 96 % des lignes écrites de la base :
 * 16 400 envois pour 116 joueurs le 2026-10-09, soit ~140 par joueur et par jour, quand l'offre
 * gratuite plafonne à 100 000 lignes écrites par jour pour tout le monde (mesuré par `wrangler
 * d1 insights`). À trente secondes, le plafond tombait vers 700 joueurs actifs ; à deux minutes,
 * une partie continue envoie quatre fois moins. Ce que ça coûte : la copie du cloud a jusqu'à deux
 * minutes de retard PENDANT qu'on joue — jamais à la sortie, que flushSync() pousse aussitôt, ni
 * à la victoire, qui passe devant la file (pushSoon). Les APK déjà installés gardent leurs trente
 * secondes : le gain arrive à mesure que les joueurs mettent à jour.
 */
const PUSH_MS = 120_000;

/** A puzzle can't be opened while the network hangs. Past this the local copy is used, which is
 *  at worst behind — and the union on the next push loses nothing either way. */
const PULL_MS = 5_000;

/** Prévenu quand le serveur vient d'apprendre une victoire — les paliers ont pu bouger, la
 *  célébration (src/celebrate.ts) redemande l'état. Injecté par wireSync() et non importé :
 *  celebrate.ts tire profile.ts, qui tire prefs.ts, qui importe déjà ce module-ci. */
let onWin: () => void = () => {};

/** One re-mint per session at most: a token the server rejects is dropped and replaced once,
 *  never in a loop against a backend that answers 401 to everything. */
let reminted = false;

/** Turns a 401 into a fresh account. Returns true if the caller may retry. */
async function recover(err: unknown): Promise<boolean> {
  if (!(err instanceof Unauthorized) || reminted) return false;
  reminted = true;
  await forgetUser();
  return !!(await ensureUser());
}

async function push(id: string, snap: Snapshot): Promise<void> {
  const token = await ensureUser();
  if (!token) return; // offline, or minting failed: the local save stands
  try {
    adoptRemote(id, await pushRemoteProgress(token, id, snap));
  } catch (err) {
    // Une création supprimée ailleurs (cf. pushRemoteProgress) : sa sauvegarde part avec elle.
    if (err instanceof HttpError && err.status === 404) return forgetProgress(id);
    if (!(await recover(err))) return;
    const fresh = await currentUser();
    if (!fresh) return;
    try {
      adoptRemote(id, await pushRemoteProgress(fresh, id, snap));
    } catch {
      return; /* the next flush will carry the same complete list */
    }
  }
  // Seulement une feuille FINIE : c'est la victoire qui fait monter un palier, et une partie en
  // cours pousse toutes les trente secondes — autant de requêtes pour rien.
  // Les partages en attente repartent d'abord : un partage fait sur la carte de fin arrive souvent
  // AVANT cette victoire, le serveur l'a refusé (409), et il attendait le lancement suivant.
  if (snap.done) void flushShares().finally(onWin);
}

// One timer per puzzle, holding the LATEST snapshot: a save that supersedes another before the
// window elapses simply replaces it, since each one is complete in itself.
const queued = new Map<string, { snap: Snapshot; timer: number }>();

function pushSoon(id: string, snap: Snapshot): void {
  if (isCreation(id)) return;
  const waiting = queued.get(id);
  if (waiting) {
    waiting.snap = snap;
    // Finishing jumps the queue: it is the state worth never losing, and it is also the last
    // one the player will produce — holding it for the window risks the app being closed first.
    if (!snap.done) return;
    clearTimeout(waiting.timer);
    queued.delete(id);
    void push(id, snap);
    return;
  }
  if (snap.done) {
    void push(id, snap);
    return;
  }
  const timer = setTimeout(() => {
    const entry = queued.get(id);
    queued.delete(id);
    if (entry) void push(id, entry.snap);
  }, PUSH_MS) as unknown as number;
  queued.set(id, { snap, timer });
}

/**
 * Starting over. The queued push is dropped first and not merely ignored: it carries the game
 * that was just thrown away, and arriving after the delete it would put it right back.
 */
function resetRemote(id: string): void {
  // Une création AUSSI, contrairement aux envois : le serveur garde peut-être encore sa partie
  // (rapatriement pas encore fait, cf. importServerCreations dans home.ts), et le rapatriement
  // suivant la ramènerait — « Recommencer » serait défait (revue du 2026-10-30). Effacer n'envoie
  // rien de la création, et un 404 est toléré quand il n'y a plus rien là-bas.
  const waiting = queued.get(id);
  if (waiting) {
    clearTimeout(waiting.timer);
    queued.delete(id);
  }
  void (async () => {
    const token = await currentUser();
    if (!token) return; // no account: nothing was ever pushed
    try {
      await deleteRemoteProgress(token, id);
    } catch (err) {
      await recover(err); // dropped: the reset stands locally, the cloud will catch up
    }
  })();
}

/**
 * Oublier l'envoi en attente d'une feuille qui n'existe plus — une création que le joueur vient de
 * supprimer (cf. withDelete dans home.ts). Même geste que le début de resetRemote() : l'envoi
 * attendu porte la partie de la feuille supprimée, et parti après la suppression il la
 * recréerait (le serveur le refuse désormais, cf. PUT /me/progress, mais autant ne pas l'envoyer).
 */
export function cancelPush(id: string): void {
  const waiting = queued.get(id);
  if (!waiting) return;
  clearTimeout(waiting.timer);
  queued.delete(id);
}

/** Flushes the coalescing window at once — the app being backgrounded is the usual way a game
 *  ends on a phone, and the whole pending window would go with it. */
export function flushSync(): void {
  for (const [id, entry] of queued) {
    clearTimeout(entry.timer);
    void push(id, entry.snap);
  }
  queued.clear();
}

/**
 * The cloud copy of one puzzle, adopted locally. Bounded in time: opening a puzzle is the one
 * place the player is actually waiting on this.
 */
export async function pullPuzzle(id: string): Promise<void> {
  const token = await currentUser();
  if (!token) return; // no account yet: there is nothing in the cloud to pull
  const remote = await Promise.race([
    fetchRemoteProgress(token, id).catch(async (err) => {
      await recover(err);
      return null;
    }),
    new Promise<null>((r) => setTimeout(() => r(null), PULL_MS)),
  ]);
  if (remote) adoptRemote(id, remote);
}

/**
 * Rapatrier la sauvegarde d'une création que le serveur gardait encore, AVANT qu'il ne l'efface
 * (cf. importServerCreations dans home.ts). Le serveur fusionne par union : on lui pousse d'abord
 * ce que cet appareil a, et sa réponse — l'union — devient la copie locale. Sans copie locale, on
 * tire simplement la sienne. Lève sur tout échec : l'appelant ne doit RIEN effacer là-bas tant
 * que la partie n'est pas en sûreté ici.
 */
export async function settleCreation(token: string, id: string): Promise<void> {
  const local = localSnapshots().find((s) => s.id === id);
  if (local && local.snap.taps) {
    adoptRemote(id, await pushRemoteProgress(token, id, local.snap));
    return;
  }
  const remote = await fetchRemoteProgress(token, id);
  if (remote) adoptRemote(id, remote);
}

let once: Promise<void> | null = null;

/**
 * Launch pass, memoised so the catalogue can await it in parallel with its own request without
 * ever running it twice.
 *
 * The push side matters as much as the pull: it is what carries the saves of a player who was
 * playing before this existed, and what repairs a push the system killed on the way out. A
 * local record is sent when the server doesn't know it or knows less of it — the server-side
 * union then makes the operation harmless even when the comparison is wrong.
 */
export function syncOnce(): Promise<void> {
  return (once ??= run());
}

async function run(): Promise<void> {
  const local = localSnapshots();
  // No account and nothing to save: don't create one just to ask an empty question.
  const token = local.length ? await ensureUser() : await currentUser();
  // Sortie SANS avoir rien réconcilié : même raison que le catch plus bas, et ce chemin-ci est le
  // plus fréquent des deux — c'est celui d'un lancement hors ligne avec des sauvegardes à pousser,
  // où `ensureUser()` n'a pas pu créer de compte. Sans cette remise à zéro, la session entière
  // reste sur cet échec silencieux.
  if (!token) {
    once = null;
    return;
  }
  try {
    const index = await fetchProgressIndex(token);
    mergeRemoteIndex(index);
    const remote = new Map(index.map((r) => [r.puzzleId, r]));
    for (const { id, snap } of local) {
      if (!snap.taps) continue; // nothing to contribute
      if (isCreation(id)) continue; // gardée sur le téléphone, cf. l'en-tête
      const r = remote.get(id);
      if (r && r.fp === snap.fp && r.pct >= snap.pct) continue;
      await push(id, snap); // sequential: a launch must not fire twenty requests at once
    }
    // Les partages en attente partent APRÈS les victoires : le serveur refuse un partage dont il
    // ne connaît pas encore la fin (cf. src/shares.ts), et c'est la boucle juste au-dessus qui la
    // lui apprend.
    // Pas de demande de badges ici, et c'est une mesure : une par lancement et par joueur, c'était
    // un quart des appels à GET /me/badges pour rien. Les victoires d'une session hors ligne
    // passent par push() juste au-dessus, qui prévient la célébration elle-même ; le reste
    // attend l'ouverture du trophée.
    void flushShares();
  } catch (err) {
    // UNE PASSE RATÉE NE DOIT PAS TENIR TOUTE LA SESSION. `once` mémoïse la passe de lancement
    // pour qu'elle ne parte pas deux fois ; mémoïser son ÉCHEC est autre chose — un lancement
    // sans réseau condamnait alors la session entière à ne jamais se réconcilier, même une fois
    // le réseau revenu, jusqu'au prochain démarrage de l'application. Sur un jeu qui a besoin du
    // réseau pour que l'avancement remonte, c'est exactement ce qu'il ne faut pas.
    //
    // Le prochain appel repartira donc, et il n'y a pas de boucle à craindre : syncOnce() n'est
    // appelée qu'au chargement du catalogue, une fois par passage.
    await recover(err);
    once = null;
  }
}

/**
 * Tout resynchroniser depuis zéro, parce que le jeton de cet appareil vient de changer sous les
 * pieds de tout le monde : un code de reprise a été échangé (cf. adoptUser dans account.ts).
 *
 * `once` est remis à null parce que la passe de lancement est mémoïsée — sans ça, le compte qu'on
 * vient de retrouver ne serait synchronisé qu'au prochain démarrage de l'application. Et rien
 * d'autre n'a eu besoin d'être écrit pour la reprise : `run()` fait déjà exactement ce qu'elle
 * demande, dans cet ordre. Elle TIRE l'index du compte retrouvé — les pourcentages du catalogue,
 * et le drapeau `stale` qui fera chercher la liste de taps à l'ouverture d'un puzzle — puis elle
 * POUSSE ce que cet appareil garde en local. Le serveur en fait l'union : ce qui a été peint ici
 * avant la reprise n'est pas perdu, et ce qui vient de l'ancien téléphone arrive.
 *
 * `reminted` repart aussi : c'est un budget d'un re-mint par session contre une boucle de 401, et
 * une nouvelle identité mérite le sien.
 */
export async function resyncAll(): Promise<void> {
  once = null;
  reminted = false;
  await syncOnce();
}

export function wireSync(badgesMayHaveChanged: () => void): void {
  onWin = badgesMayHaveChanged;
  // The admin embeds the game to build a grid, never to play one: no account, no saves.
  setProgressSync(pushSoon, resetRemote);
  // Le stockage du téléphone plein MÊME après avoir libéré ce qui pouvait l'être (cf. writeOne
  // dans progress.ts) : le joueur continue sans sauvegarde locale, et sans ce rapport on ne
  // l'apprendrait que par un avis sur le Store — c'est ainsi qu'on l'a appris le 2026-10-15.
  setStorageFullReport(({ id, chars, freed }) =>
    reportClientError({
      msg: 'storage_full',
      src: `progress:${id}`,
      stack: `chars=${chars} freed=${freed}`,
      version: __VERSION__,
      when: 'play',
    }),
  );
  addEventListener('pagehide', flushSync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushSync();
  });
  void syncOnce();
}
